import type {
  AutomationAgentTokenDto,
  AutomationConnectionDto,
  AutomationTestRunReachDto,
} from '@tms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge, Button, Dialog, ErrorState, Field, Input, SkeletonRows } from '../../components/ui';
import { useErrorText, useFormat, useT } from '../../i18n';
import { api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

const connectionKey = [...queryKeys.automation.all, 'connection'] as const;
const agentTokenKey = [...queryKeys.automation.all, 'agent-token'] as const;

/** A value to copy, with a button that says it is copied. */
const CopyBlock = ({ value }: { value: string }) => {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const copy = () =>
    void navigator.clipboard.writeText(value).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => toast.error(t('common.linkCopyFailed')),
    );
  return (
    <div className="flex items-start gap-2">
      <code className="min-w-0 flex-1 break-all rounded-md border border-border bg-sunken px-2 py-1.5 font-mono text-[12px] text-ink">
        {value}
      </code>
      <Button size="sm" onClick={copy} aria-label={t('automation.connection.copy')}>
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        {copied ? t('automation.connection.copied') : t('automation.connection.copy')}
      </Button>
    </div>
  );
};

/**
 * How TMS reaches TestRun and GitLab, without cluster secrets: TMS keeps its
 * own signing key (TestRun is given the public half) and the GitLab token an
 * admin pastes here. Secrets set in the cluster take precedence and show so.
 */
export const ConnectionDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useT();
  const { dateTime } = useFormat();
  const { errorText } = useErrorText();
  const queryClient = useQueryClient();
  const [token, setToken] = useState('');
  const connection = useQuery({
    queryKey: connectionKey,
    queryFn: ({ signal }) =>
      api.get<AutomationConnectionDto>('/automation/connection', undefined, signal),
    enabled: open,
  });
  // the server's own way to TestRun (DEV → stage): a scheduled run has no browser to take pool accounts with
  const reach = useMutation({
    mutationFn: () => api.post<AutomationTestRunReachDto>('/automation/connection/testrun-check'),
    onError: (error) => toast.error(errorText(error)),
  });
  const saveToken = useMutation({
    mutationFn: (value: string) =>
      api.put<{ ok: true }>('/automation/connection/gitlab-token', { token: value }),
    onSuccess: (_result, value) => {
      setToken('');
      toast.success(
        value ? t('automation.connection.tokenSaved') : t('automation.connection.tokenRemoved'),
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.automation.all });
    },
    onError: (error) => toast.error(errorText(error)),
  });

  const agentToken = useQuery({
    queryKey: agentTokenKey,
    queryFn: ({ signal }) =>
      api.get<AutomationAgentTokenDto>('/automation/connection/agent-token', undefined, signal),
    enabled: open,
  });
  // the token is shown once, right after it is created, and not kept anywhere in the page after
  const [newAgentToken, setNewAgentToken] = useState<string | null>(null);
  const createAgentToken = useMutation({
    mutationFn: () => api.post<AutomationAgentTokenDto>('/automation/connection/agent-token'),
    onSuccess: (created) => {
      setNewAgentToken(created.token ?? null);
      void queryClient.invalidateQueries({ queryKey: agentTokenKey });
    },
    onError: (error) => toast.error(errorText(error)),
  });
  const removeAgentToken = useMutation({
    mutationFn: () => api.delete<{ ok: true }>('/automation/connection/agent-token'),
    onSuccess: () => {
      setNewAgentToken(null);
      toast.success(t('automation.connection.agentRemoved'));
      void queryClient.invalidateQueries({ queryKey: agentTokenKey });
    },
    onError: (error) => toast.error(errorText(error)),
  });

  const data = connection.data;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('automation.connection.title')}
      description={t('automation.connection.hint')}
      footer={<Button onClick={() => onOpenChange(false)}>{t('common.close')}</Button>}
    >
      {connection.error ? (
        <ErrorState error={connection.error} onRetry={() => void connection.refetch()} />
      ) : !data ? (
        <SkeletonRows rows={3} />
      ) : (
        <div className="flex flex-col gap-6 text-[13px]">
          <section className="space-y-2">
            <h3 className="font-semibold text-ink">{t('automation.connection.testRun')}</h3>
            <p className="text-ink-muted">
              {data.testRunUrl
                ? t('automation.connection.testRunUrl', { url: data.testRunUrl })
                : t('automation.connection.testRunOff')}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                onClick={() => reach.mutate()}
                loading={reach.isPending}
                disabled={reach.isPending}
              >
                {t('automation.connection.reachCheck')}
              </Button>
              {reach.data ? (
                <span className={reach.data.reachable ? 'text-pass' : 'text-fail'}>
                  {reach.data.reachable
                    ? t('automation.connection.reachOk', {
                        status: reach.data.status ?? '',
                        ms: reach.data.ms ?? '',
                      })
                    : t(`automation.connection.reach.${reach.data.reason ?? 'network'}`, {
                        status: reach.data.status ?? '',
                      })}
                </span>
              ) : null}
            </div>
            {reach.data && !reach.data.reachable && reach.data.detail ? (
              <p className="text-[12px] text-ink-subtle">
                {t('automation.connection.reachDetail', { detail: reach.data.detail })}
              </p>
            ) : null}
            <p className="text-ink-muted">{t('automation.connection.publicKeyHint')}</p>
            <CopyBlock value={`TMS_PUBLIC_KEY: "${data.publicKeyLine}"`} />
            <p className="text-[12px] text-ink-subtle">
              {data.keySource === 'env'
                ? t('automation.connection.keyFromSecret')
                : t('automation.connection.keyFromTms')}
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="flex items-center gap-2 font-semibold text-ink">
              {t('automation.connection.gitlab')}
              <Badge
                className={
                  data.gitlab.tokenSource ? 'bg-pass-soft text-pass' : 'bg-block-soft text-block'
                }
              >
                {data.gitlab.tokenSource
                  ? t('automation.connection.tokenSet')
                  : t('automation.connection.tokenMissing')}
              </Badge>
            </h3>
            <p className="text-ink-muted">
              {t('automation.connection.gitlabHint', { url: data.gitlab.url })}
            </p>
            {data.gitlab.tokenSource === 'env' ? (
              <p className="text-ink-muted">{t('automation.connection.tokenFromSecret')}</p>
            ) : (
              <>
                {data.gitlab.tokenSource === 'db' && data.gitlab.setBy ? (
                  <p className="text-[12px] text-ink-subtle">
                    {t('automation.connection.tokenSetBy', {
                      who: data.gitlab.setBy,
                      when: data.gitlab.setAt ? dateTime(data.gitlab.setAt) : '',
                    })}
                  </p>
                ) : null}
                <form
                  className="flex flex-wrap items-end gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (token.trim()) saveToken.mutate(token.trim());
                  }}
                >
                  <Field label={t('automation.connection.tokenLabel')} className="min-w-60 flex-1">
                    {({ id }) => (
                      <Input
                        id={id}
                        type="password"
                        autoComplete="off"
                        value={token}
                        onChange={(event) => setToken(event.target.value)}
                        placeholder="glpat-…"
                      />
                    )}
                  </Field>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!token.trim() || saveToken.isPending}
                  >
                    {data.gitlab.tokenSource
                      ? t('automation.connection.replace')
                      : t('common.save')}
                  </Button>
                  {data.gitlab.tokenSource === 'db' ? (
                    <Button
                      variant="danger"
                      disabled={saveToken.isPending}
                      onClick={() => saveToken.mutate('')}
                    >
                      {t('automation.connection.remove')}
                    </Button>
                  ) : null}
                </form>
              </>
            )}
          </section>

          <section className="space-y-2">
            <h3 className="flex items-center gap-2 font-semibold text-ink">
              {t('automation.connection.agent')}
              <Badge
                className={
                  agentToken.data?.set ? 'bg-pass-soft text-pass' : 'bg-block-soft text-block'
                }
              >
                {agentToken.data?.set
                  ? t('automation.connection.tokenSet')
                  : t('automation.connection.tokenMissing')}
              </Badge>
            </h3>
            <p className="text-ink-muted">{t('automation.connection.agentHint')}</p>
            {agentToken.data?.set && agentToken.data.setBy ? (
              <p className="text-[12px] text-ink-subtle">
                {t('automation.connection.agentSet', {
                  who: agentToken.data.setBy,
                  when: agentToken.data.setAt ? dateTime(agentToken.data.setAt) : '',
                })}
              </p>
            ) : null}
            {newAgentToken ? (
              <>
                <CopyBlock value={`TMS_AGENT_TOKEN=${newAgentToken}`} />
                <p className="text-[12px] text-block">
                  {t('automation.connection.agentShownOnce')}
                </p>
              </>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => createAgentToken.mutate()}
                loading={createAgentToken.isPending}
                disabled={removeAgentToken.isPending}
              >
                {agentToken.data?.set
                  ? t('automation.connection.agentReplace')
                  : t('automation.connection.agentCreate')}
              </Button>
              {agentToken.data?.set ? (
                <Button
                  variant="danger"
                  onClick={() => removeAgentToken.mutate()}
                  disabled={createAgentToken.isPending || removeAgentToken.isPending}
                >
                  {t('automation.connection.agentRemove')}
                </Button>
              ) : null}
            </div>
          </section>
        </div>
      )}
    </Dialog>
  );
};
